import React, { useState, useEffect } from 'react';
import { 
    Plus, Edit3, Trash2, CheckCircle, AlertTriangle, Clock, Percent 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const RegleTarif = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [regles, setRegles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [companyInfo, setCompanyInfo] = useState({ regime_tva_recuperable: 1, taux_tva: 18.0 });

  const [formData, setFormData] = useState({
    libelle: '',
    type_regle: '',
    categorie_id: '',
    mode_valeur: 'FIXE',
    montant: '',
    soumis_tva: true
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [modalConfig, setModalConfig] = useState({ show: false, action: null, message: '', id: null });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resRegles, resCat, resComp] = await Promise.all([
        API.get('/hebergement/regles-tarifs').catch(() => ({ data: [] })),
        API.get('/hebergement/categories').catch(() => ({ data: [] })),
        company_id ? API.get(`/company/${company_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);
      
      if (resComp.data?.success && resComp.data.data) {
        setCompanyInfo({
          regime_tva_recuperable: resComp.data.data.regime_tva_recuperable ?? 1,
          taux_tva: resComp.data.data.taux_tva ?? 18.0
        });
      }

      // 🛡️ CORRECTION SÉCURISÉE : Extraction garantie d'un tableau pour les règles
      const rawRegles = resRegles.data;
      let listRegles = [];
      if (Array.isArray(rawRegles)) {
        listRegles = rawRegles;
      } else if (rawRegles && typeof rawRegles === 'object') {
        listRegles = rawRegles.regles || rawRegles.data || rawRegles.items || [];
      }
      setRegles(listRegles);

      // 🛡️ CORRECTION SÉCURISÉE : Extraction garantie d'un tableau pour les catégories
      const rawCat = resCat.data;
      let listCat = [];
      if (Array.isArray(rawCat)) {
        listCat = rawCat;
      } else if (rawCat && typeof rawCat === 'object') {
        listCat = rawCat.categories || rawCat.data || rawCat.items || [];
      }
      setCategories(listCat);

    } catch (err) {
      console.error("Erreur chargement données:", err);
      showToast("Erreur de chargement", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.libelle.trim() || !formData.type_regle.trim() || formData.montant === '') {
      showToast("Veuillez remplir tous les champs obligatoires", "error");
      return;
    }

    setLoading(true);
    try {
      const isTvaApplicable = companyInfo.regime_tva_recuperable === 1 ? formData.soumis_tva : false;
      const tauxApplique = isTvaApplicable ? Number(companyInfo.taux_tva) : 0;

      const payload = {
        libelle: formData.libelle.trim(),
        type_regle: formData.type_regle.trim().toUpperCase(),
        categorie_id: formData.categorie_id || null,
        mode_valeur: formData.mode_valeur,
        montant: parseFloat(formData.montant) || 0,
        soumis_tva: isTvaApplicable ? 1 : 0,
        taux_tva: tauxApplique
      };

      if (editingId) {
        await API.put(`/hebergement/regles-tarifs/${editingId}`, payload);
        showToast("Règle mise à jour avec succès");
      } else {
        await API.post('/hebergement/regles-tarifs', payload);
        showToast("Règle créée avec succès");
      }

      closeForm();
      fetchData();
    } catch (err) {
      console.error("Erreur enregistrement règle :", err);
      showToast(err.response?.data?.error || "Erreur lors de l'enregistrement", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setModalConfig({
      show: true,
      action: () => executeDelete(id),
      message: "Voulez-vous vraiment supprimer cette règle tarifaire ?",
      id
    });
  };

  const executeDelete = async (id) => {
    setLoading(true);
    try {
      await API.delete(`/hebergement/regles-tarifs/${id}`);
      showToast("Règle supprimée avec succès");
      setModalConfig({ show: false, action: null, message: '', id: null });
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || "Impossible de supprimer cette règle.", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ libelle: '', type_regle: '', categorie_id: '', mode_valeur: 'FIXE', montant: '', soumis_tva: true });
  };

  const isEntrepriseSousTva = companyInfo.regime_tva_recuperable === 1;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Clock size={22} color="#2563eb" /> GESTION DES RÈGLES DE CHECK-IN / CHECK-OUT
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Paramétrez les frais ou taux applicables de manière générale ou par catégorie de chambre
            </div>
          </div>
          <div>
            {!isAdding && !editingId && (
              <button onClick={() => setIsAdding(true)} style={btnPrimaryStyle}>
                <Plus size={18} /> Nouvelle Règle
              </button>
            )}
          </div>
        </header>

        <div style={{ padding: '30px 40px' }}>

          {(isAdding || editingId) && (
            <div style={{ ...contentCardStyle, padding: '25px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', marginBottom: '15px', fontWeight: '800' }}>
                {editingId ? 'Modifier la règle tarifaire' : 'Ajouter une nouvelle règle tarifaire'}
              </h3>
              <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: '220px' }}>
                  <label style={labelStyle}>LIBELLÉ DE LA RÈGLE</label>
                  <input 
                    type="text" 
                    style={inputStyle} 
                    placeholder="Ex: Early Check-in Suite"
                    value={formData.libelle} 
                    onChange={(e) => setFormData({...formData, libelle: e.target.value})} 
                    required 
                  />
                </div>
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <label style={labelStyle}>TYPE DE RÈGLE</label>
                  <input 
                    type="text" 
                    style={inputStyle} 
                    placeholder="Ex: EARLY_CHECKIN"
                    value={formData.type_regle} 
                    onChange={(e) => setFormData({...formData, type_regle: e.target.value})}
                    required
                  />
                </div>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <label style={labelStyle}>CATÉGORIE CIBLÉE (OPTIONNEL)</label>
                  <select 
                    style={inputStyle} 
                    value={formData.categorie_id} 
                    onChange={(e) => setFormData({...formData, categorie_id: e.target.value})}
                  >
                    <option value="">-- Toutes les catégories (Général) --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nom || cat.libelle || `Catégorie ${cat.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <label style={labelStyle}>MODE DE CALCUL</label>
                  <select 
                    style={inputStyle} 
                    value={formData.mode_valeur} 
                    onChange={(e) => setFormData({...formData, mode_valeur: e.target.value})}
                  >
                    <option value="FIXE">Montant Fixe (F)</option>
                    <option value="POURCENTAGE">Pourcentage (%)</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '130px' }}>
                  <label style={labelStyle}>MONTANT / TAUX</label>
                  <input 
                    type="number" 
                    style={inputStyle} 
                    placeholder="Ex: 5000 ou 30"
                    value={formData.montant} 
                    onChange={(e) => setFormData({...formData, montant: e.target.value})} 
                    required 
                  />
                </div>

                {/* 🔥 SECTION TVA CONDITIONNELLE */}
                <div style={{ 
                  flex: 1.5, 
                  minWidth: '200px', 
                  opacity: isEntrepriseSousTva ? 1 : 0.5,
                  background: isEntrepriseSousTva ? '#f0fdf4' : '#f1f5f9',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  border: `1px solid ${isEntrepriseSousTva ? '#bbf7d0' : '#cbd5e1'}`
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isEntrepriseSousTva ? 'pointer' : 'not-allowed', fontWeight: '800', fontSize: '12px', color: '#166534' }}>
                    <input 
                      type="checkbox" 
                      checked={isEntrepriseSousTva ? formData.soumis_tva : false}
                      disabled={!isEntrepriseSousTva}
                      onChange={(e) => setFormData({...formData, soumis_tva: e.target.checked})}
                      style={{ width: '16px', height: '16px', cursor: isEntrepriseSousTva ? 'pointer' : 'not-allowed' }}
                    />
                    <Percent size={14}/> Soumis à la TVA ({companyInfo.taux_tva}%)
                  </label>
                  <span style={{ fontSize: '10px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                    {isEntrepriseSousTva ? `Applique ${companyInfo.taux_tva}% si coché.` : "Désactivé (Non assujetti)."}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" disabled={loading} style={btnSubmitStyle}>{loading ? '...' : 'Valider'}</button>
                  <button type="button" onClick={closeForm} style={btnCancelStyle}>Annuler</button>
                </div>
              </form>
            </div>
          )}

          <div style={contentCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Libellé</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Catégorie Ciblée</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>Valeur</th>
                  <th style={thStyle}>Régime / TVA</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {regles.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Aucune règle tarifaire enregistrée.
                    </td>
                  </tr>
                ) : (
                  regles.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...tdStyle, fontWeight: '800' }}>{item.libelle}</td>
                      <td style={tdStyle}>
                        <span style={badgeStyle}>{item.type_regle}</span>
                      </td>
                      <td style={{ ...tdStyle, color: item.categorie_nom ? '#2563eb' : '#64748b', fontWeight: item.categorie_nom ? '700' : '400' }}>
                        {item.categorie_nom ? item.categorie_nom : '🌐 Général (Toutes)'}
                      </td>
                      <td style={tdStyle}>{item.mode_valeur}</td>
                      <td style={{ ...tdStyle, fontWeight: '800', color: '#059669' }}>
                        {item.mode_valeur === 'FIXE' ? `${Number(item.montant).toLocaleString()} F` : `${item.montant} %`}
                      </td>
                      <td style={tdStyle}>
                        {item.soumis_tva ? (
                          <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800' }}>
                            TVA ({item.taux_tva || companyInfo.taux_tva}%)
                          </span>
                        ) : (
                          <span style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                            Non soumis
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => {
                              setEditingId(item.id);
                              setFormData({
                                libelle: item.libelle,
                                type_regle: item.type_regle,
                                categorie_id: item.categorie_id || '',
                                mode_valeur: item.mode_valeur,
                                montant: item.montant,
                                soumis_tva: item.soumis_tva === 1 || item.soumis_tva === true
                              });
                              setIsAdding(false);
                            }} 
                            style={actionBtnStyle('#2563eb')}
                          >
                            <Edit3 size={14}/>
                          </button>
                          <button onClick={() => handleDeleteClick(item.id)} style={actionBtnStyle('#ef4444')}>
                            <Trash2 size={14}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {modalConfig.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '50%' }}><AlertTriangle size={24} /></div>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Confirmation de suppression</h2>
            </div>
            <p style={{ color: '#475569', marginBottom: '30px' }}>{modalConfig.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalConfig({ show: false, action: null, message: '', id: null })} style={btnSecondaryStyle}>Annuler</button>
              <button onClick={modalConfig.action} style={btnDangerStyle}>
                {loading ? '...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES HARMONISÉS ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' };
const btnSecondaryStyle = { border: '1px solid #e2e8f0', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', background: 'white' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', marginTop: '5px', background: '#fff' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const btnSubmitStyle = { padding: '0 25px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', height: '45px' };
const btnCancelStyle = { padding: '0 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', height: '45px' };
const actionBtnStyle = (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '900', cursor: 'pointer' });
const badgeStyle = { padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900', background: '#e0f2fe', color: '#0369a1' };
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', padding: '30px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const btnDangerStyle = { background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' };

export default RegleTarif;